import { Box } from "@mui/system";
import Sidebar from "./Sidebar";
import { Routes, Route } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import NotFound from "./pages/NotFound";
import { useEffect, useState } from "react";
import { LoginDetail, useLogin, VersionInfo } from "providers/Login";
import { useNavigate } from "react-router";
import Loading from "components/Loading";
import Configuration from "./pages/Configuration";
import ApiKeys from "./pages/ApiKeys";
import Integrations from "./pages/Integrations";
import { Container } from "@mui/material";
import Apps from "./pages/Apps";
import Obfuscations from "./pages/Obfuscations";
import DataTypes from "./pages/DataTypes";
import MaskFormats from "./pages/MaskFormats";
import Rules from "./pages/Rules";
import Activites from "./pages/Activity";
import IcapConfiguration from "./pages/Icap";
import Groups from "./pages/Groups";
import Alerts from "./pages/AlertPolicy";
import AlertLogs from "./pages/AlertLog";
import AlertNotification from "./pages/AlertNotification";
import ImportExport from "./pages/ImportExport";
import ScanningMode from "./pages/ScanningMode";
import Services from "./pages/Services";
import ServiceDetailPage from "./pages/Services/Detail";
import ActiveSessions from "./pages/ActiveSessions";
import AdminUserSessions from "./pages/AdminLogins/sessions";
import DirectoryGroups from "./pages/Integrations/DirectoryGroups";
import NoPermission from "./pages/NoPermission";
import { useRole } from "hooks/useRole";
import { ALL_ROLES, FULL_ADMIN_ROLES, POLICY_EDITOR_ROLES, Role } from "helpers/roles";

// NS-753
//RequireRole shows the page only to the roles given. The server enforces the
//same rules. This only replaces a page of errors with a clear message.
function RequireRole(props: { roles: readonly Role[], children: React.ReactElement }) {
    const { role } = useRole();

    return props.roles.includes(role) ? props.children : <NoPermission />;
}

function App() {
    const login = useLogin();
    const navigate = useNavigate();
    const [finished, setFinished] = useState(false);
    const [user, setUser] = useState({} as LoginDetail);
    const [versionInfo, setVersionInfo] = useState<VersionInfo | undefined>();

    const init = async () => {
        const detail = await login.GetLoginDetail();
        if (!detail) {
            await login.Logout();
            navigate("/login");
            return;
        }

        const versionInfo = await login.FetchVersion();


        setUser(detail);
        setVersionInfo(versionInfo);
        setFinished(true);
    };
    useEffect(() => {
        init();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Loading finished={finished}>
            <Box sx={{ display: "flex", height: "100vh" }}>
                <Sidebar user={user} versionInfo={versionInfo} />
                <Box sx={{ height: "100vh", overflow: "auto", flexGrow: "1" }}>
                    <Container maxWidth={false} sx={{ height: "100%", padding: "80px !important" }}>
                        <Routes>
                            {/* NS-753 */}
                            <Route path="/" element={<RequireRole roles={ALL_ROLES}><Dashboard /></RequireRole>} />
                            <Route path="/configuration" element={<RequireRole roles={FULL_ADMIN_ROLES}><Configuration /></RequireRole>} />
                            <Route path="/icap" element={<RequireRole roles={FULL_ADMIN_ROLES}><IcapConfiguration /></RequireRole>} />
                            <Route path="/users" element={<RequireRole roles={FULL_ADMIN_ROLES}><ActiveSessions /></RequireRole>} />
                            <Route path="/apikeys" element={<RequireRole roles={FULL_ADMIN_ROLES}><ApiKeys /></RequireRole>} />
                            <Route path="/adminlogins/:username" element={<RequireRole roles={FULL_ADMIN_ROLES}><AdminUserSessions /></RequireRole>} />
                            <Route path="/directory" element={<RequireRole roles={FULL_ADMIN_ROLES}><Integrations /></RequireRole>} />
                            <Route path="/directory/:id/groups" element={<RequireRole roles={FULL_ADMIN_ROLES}><DirectoryGroups /></RequireRole>} />
                            <Route path="/alertnotification" element={<RequireRole roles={FULL_ADMIN_ROLES}><AlertNotification /></RequireRole>} />
                            <Route path="/applications" element={<RequireRole roles={ALL_ROLES}><Apps /></RequireRole>} />
                            <Route path="/obfuscations" element={<RequireRole roles={ALL_ROLES}><Obfuscations /></RequireRole>} />
                            <Route path="/maskformats" element={<RequireRole roles={ALL_ROLES}><MaskFormats /></RequireRole>} />
                            <Route path="/activities" element={<RequireRole roles={ALL_ROLES}><Activites /></RequireRole>} />
                            <Route path="/datatypes" element={<RequireRole roles={ALL_ROLES}><DataTypes /></RequireRole>} />
                            <Route path="/alerts" element={<RequireRole roles={ALL_ROLES}><Alerts /></RequireRole>} />
                            <Route path="/alertlogs" element={<RequireRole roles={ALL_ROLES}><AlertLogs /></RequireRole>} />
                            <Route path="/rules" element={<RequireRole roles={ALL_ROLES}><Rules /></RequireRole>} />
                            <Route path="/groups" element={<RequireRole roles={POLICY_EDITOR_ROLES}><Groups /></RequireRole>} />
                            <Route path="/import-export" element={<RequireRole roles={FULL_ADMIN_ROLES}><ImportExport /></RequireRole>} />
                            <Route path="/scanning-mode" element={<RequireRole roles={FULL_ADMIN_ROLES}><ScanningMode /></RequireRole>} />
                            <Route path="/services" element={<RequireRole roles={FULL_ADMIN_ROLES}><Services /></RequireRole>} />
                            <Route path="/services/:id" element={<RequireRole roles={FULL_ADMIN_ROLES}><ServiceDetailPage /></RequireRole>} />
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </Container>
                </Box>
            </Box>
        </Loading>
    );
}

export default App;
